import React, { useEffect, useState } from "react";
import { api, downloadCsv, formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import ProgressBar from "@/components/common/ProgressBar";
import { Trophy, Airplane, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Leaderboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overall"); // overall | milestone | bali | almost

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/leaderboard"); setRows(data); } catch {}
      setLoading(false);
    })();
  }, []);

  const filtered = (() => {
    if (tab === "milestone") return rows.filter((r) => (r.current_milestone || 0) >= 25);
    if (tab === "bali") return rows.filter((r) => r.status === "bali_qualified" || (r.total_valid_lot || 0) >= 200);
    if (tab === "almost") {
      return rows.filter((r) => {
        const l = r.total_valid_lot || 0;
        return (l >= 20 && l < 25) || (l >= 45 && l < 50) || (l >= 90 && l < 100) || (l >= 180 && l < 200);
      });
    }
    return rows;
  })();

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto">
      <PageHeader
        title="Leaderboard"
        subtitle="Internal admin view — full participant details."
        breadcrumbs="Leaderboard"
        testId="leaderboard-page"
        actions={
          <button
            data-testid="leaderboard-export-btn"
            onClick={async () => {
              try { await downloadCsv("/exports/leaderboard.csv"); toast.success("Leaderboard exported"); }
              catch (e) { toast.error(formatApiError(e)); }
            }}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-white/10 text-gray-200 hover:bg-white/5 text-sm"
          >
            <DownloadSimple size={14} /> Export CSV
          </button>
        }
      />

      <div className="flex gap-2 mb-4">
        {[
          ["overall", "Overall"],
          ["milestone", "Milestone Achievers"],
          ["bali", "Bali Qualifiers"],
          ["almost", "Almost There"],
        ].map(([k, label]) => (
          <button
            key={k}
            data-testid={`leaderboard-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm rounded-md border transition-colors ${tab === k ? "bg-blue-500/10 border-blue-500 text-blue-300" : "border-white/10 text-gray-400 hover:text-white"}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="bg-[#111827] border border-white/10 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-[#1F2937]/50 text-left text-[10px] uppercase tracking-widest text-gray-500">
                <th className="px-4 py-3 w-16">Rank</th>
                <th className="px-4 py-3">Participant</th>
                <th className="px-4 py-3">Account</th>
                <th className="px-4 py-3 text-right">Valid Lot</th>
                <th className="px-4 py-3 min-w-[200px]">Progress</th>
                <th className="px-4 py-3 text-center">Milestone</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Risk</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={8} className="p-8 text-center text-gray-500">Loading…</td></tr> :
                filtered.length === 0 ? <tr><td colSpan={8} className="p-8 text-center text-gray-500">No traders match</td></tr> :
                filtered.map((p) => (
                  <tr key={p.id} data-testid={`leaderboard-row-${p.rank}`} className={`border-b border-white/5 hover:bg-white/[0.02] ${p.rank <= 3 ? "bg-orange-500/5" : ""}`}>
                    <td className="px-4 py-3">
                      {p.rank <= 3 ? <Trophy weight="fill" size={20} className={p.rank === 1 ? "text-yellow-400" : p.rank === 2 ? "text-gray-300" : "text-orange-400"} /> :
                        <span className="font-mono text-gray-400">#{p.rank}</span>}
                    </td>
                    <td className="px-4 py-3 text-white font-medium">{p.full_name}</td>
                    <td className="px-4 py-3 font-mono text-gray-400 text-xs">{p.broker_account_number}</td>
                    <td className="px-4 py-3 text-right font-mono text-white">{(p.total_valid_lot || 0).toFixed(2)}</td>
                    <td className="px-4 py-3"><ProgressBar value={p.total_valid_lot || 0} /></td>
                    <td className="px-4 py-3 text-center font-mono text-orange-300">{p.current_milestone || 0}</td>
                    <td className="px-4 py-3">
                      {p.status === "bali_qualified" ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-300 border border-orange-500/20"><Airplane size={10} weight="fill" /> Bali</span> : <StatusBadge status={p.status} />}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={p.risk_status || "healthy"} /></td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
